import {
  useWSModalCustom,
  WSLoader,
  WSModal,
  WSPage
} from "@wingspanhq/fe-component-library";
import { useRequirementsConfig } from "../../useRequirementsConfig";
import { RequirementContextType } from "../../types";
import { RequirementDetailsInstructions } from "./RequirementDetailsInstructions";
import { useRequirementDefinitionQuery } from "../../../../query/requirementDefinitions/query/useRequirementDefinitionQuery";
import { useSharedFileSettingsQuery } from "../../../../query/sharedFile/query/useSharedFileSettingsQuery";
import { RequirementType } from "@wingspanhq/payments/dist/interfaces";
import { useEffect } from "react";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces/api/requirement";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { IDENTITY_VERIFICATION_REQUIREMENT_INSTRUCTIONS } from "../../modules/IdentityVerification/instructions";
import { useRequirementQuery } from "../../../../query/requirements/queries/useRequirementQuery";
import { useInsuranceCoverageMonitorQuery } from "../../../../query/insuranceCoverageMonitor/queries/useInsuranceCoverageMonitorQuery";
import { getRequirementDefinitionTemplateByParams } from "../../../RequirementDefinitions/utils";

type RequirementInstructionsModalProps = {
  onClose: (result?: boolean) => void;
  requirementId: string;
};
type RequirementInstructionsModalContentProps = {
  onClose: (result?: boolean) => void;
  requirement: IRequirementResponse;
};

const InstructionsModalContent: React.FC<
  RequirementInstructionsModalContentProps
> = ({ requirement, onClose }) => {
  const requirementConfig = useRequirementsConfig({
    requirements: [requirement],
    contextType: RequirementContextType.Payer
  });

  const queryRequirementDefinition = useRequirementDefinitionQuery(
    requirement.requirementDefinitionId!,
    {
      enabled: !!requirement.requirementDefinitionId
    }
  );

  const requirementDefinition = queryRequirementDefinition.data;

  const querySharedFilesSettings = useSharedFileSettingsQuery(
    requirementDefinition?.dataSourceId!,
    {
      enabled:
        !!requirementDefinition?.dataSourceId &&
        requirementDefinition?.requirementType === RequirementType.SharedFile
    }
  );

  const queryMonitor = useInsuranceCoverageMonitorQuery(
    requirement.dataSourceId!,
    {
      enabled:
        !!requirement.dataSourceId &&
        requirement.requirementType === RequirementType.InsuranceCoverage
    }
  );

  useEffect(() => {
    if (
      requirementDefinition?.requirementType === RequirementType.SharedFile &&
      querySharedFilesSettings.isSuccess &&
      querySharedFilesSettings.data
    ) {
      const template = getRequirementDefinitionTemplateByParams({
        type: RequirementType.SharedFile,
        contextType: querySharedFilesSettings.data?.contextType
      });

      const isInstructionsExist =
        !!template.instructions ||
        !!querySharedFilesSettings.data?.instructions;

      if (!isInstructionsExist) {
        onClose(true);
      }
    }
  }, [
    querySharedFilesSettings.dataUpdatedAt,
    requirementDefinition?.requirementType,
    onClose
  ]);

  if (requirement.requirementType === RequirementType.IdentityVerification) {
    return (
      <WSModal
        fullScreen
        titleAlign="center"
        showCloseIcon={true}
        onClose={onClose}
        title={requirementConfig.getRequirementName(requirement)}
      >
        <WSPage>
          <RequirementDetailsInstructions
            {...requirementConfig}
            onSuccessAction={() => onClose(true)}
            onCloseAction={() => onClose(false)}
            instructions={IDENTITY_VERIFICATION_REQUIREMENT_INSTRUCTIONS}
            contextType={RequirementContextType.Payer}
            requirement={requirement}
          />
        </WSPage>
      </WSModal>
    );
  } else if (requirement.requirementType === RequirementType.SharedFile) {
    const template = getRequirementDefinitionTemplateByParams({
      type: RequirementType.SharedFile,
      contextType: querySharedFilesSettings.data?.contextType
    });

    const instructions =
      template.instructions ||
      querySharedFilesSettings.data?.instructions ||
      "";

    return (
      <WSModal
        fullScreen
        titleAlign="center"
        showCloseIcon={!querySharedFilesSettings.isLoading}
        onClose={onClose}
        title={requirementConfig.getRequirementName(requirement)}
      >
        <WSPage>
          {querySharedFilesSettings.isLoading ? (
            <WSLoader.Spinner size="L" />
          ) : (
            <RequirementDetailsInstructions
              {...requirementConfig}
              onSuccessAction={() => onClose(true)}
              onCloseAction={() => onClose(false)}
              instructions={instructions}
              contextType={RequirementContextType.Payer}
              requirement={requirement}
            />
          )}
        </WSPage>
      </WSModal>
    );
  } else if (
    requirement.requirementType === RequirementType.InsuranceCoverage
  ) {
    return (
      <WSModal
        fullScreen
        titleAlign="center"
        showCloseIcon={!requirementConfig.isLoading && !queryMonitor.isLoading}
        onClose={onClose}
        title={requirementConfig.getRequirementName(requirement)}
      >
        <WSPage>
          {requirementConfig.isLoading || queryMonitor.isLoading ? (
            <WSLoader.Spinner size="L" />
          ) : requirementConfig.getRequirementInstructions(requirement) ? (
            <RequirementDetailsInstructions
              {...requirementConfig}
              onSuccessAction={() => onClose(true)}
              onCloseAction={() => onClose(false)}
              instructions={""}
              contextType={RequirementContextType.Payer}
              requirement={requirement}
              monitor={queryMonitor.data}
            />
          ) : null}
        </WSPage>
      </WSModal>
    );
  } else {
    return (
      <WSModal
        fullScreen
        titleAlign="center"
        onClose={onClose}
        title="Error"
        allowClickOutsideToClose={false}
      >
        <WSErrorMessage contextKey="Other" error="Something went wrong" />
      </WSModal>
    );
  }
};

const RequirementInstructionsModal: React.FC<
  RequirementInstructionsModalProps
> = ({ onClose, requirementId }) => {
  const queryRequirement = useRequirementQuery(requirementId);

  if (queryRequirement.isLoading) {
    return (
      <WSModal
        fullScreen
        titleAlign="center"
        showCloseIcon={false}
        allowClickOutsideToClose={false}
        title="Loading..."
      >
        <WSLoader.Spinner size="M" />
      </WSModal>
    );
  } else if (queryRequirement.isError) {
    return (
      <WSModal
        fullScreen
        titleAlign="center"
        onClose={onClose}
        title="Error"
        allowClickOutsideToClose={false}
      >
        <WSErrorMessage contextKey="Other" error="Something went wrong" />
      </WSModal>
    );
  } else {
    return (
      <InstructionsModalContent
        onClose={onClose}
        requirement={queryRequirement.data}
      />
    );
  }
};

export const useRequirementInstructionsModal = () =>
  useWSModalCustom(RequirementInstructionsModal);
